import { Link } from 'react-router-dom'
import React, { useEffect, useState } from 'react'
import '../styles/index.css'
import { useLogout } from '../hooks/useLogout'
import { useAuthContext } from '../hooks/useAuthContext'
import logo from '../images/artization_logo.png'
import { FaHome, FaImage, FaEdit, FaCog, FaSignOutAlt, FaUser, FaBlog, FaSearch, FaStore } from 'react-icons/fa'
import { useLocation } from 'react-router-dom'
import SearchModal from './SearchModal'

const Navbar = () => {
  const { logout } = useLogout()
  const { user } = useAuthContext()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const location = useLocation()

  const sidebarExpanded = isExpanded || isMobileNavOpen
  const userPhoto =
    user?.photo || user?.profileImage || user?.avatar || user?.image || user?.imageUrl
  const userInitial =
    (user?.firstName?.[0] || user?.lastName?.[0] || user?.email?.[0] || '?').toUpperCase()

  const handleClick = () => {
    setIsMobileNavOpen(false)
    logout()
  }

  const NavItem = ({ to, icon, label, condition = true }) => {
    if (!condition) return null
    return (
      <Link
        to={to}
        className="flex justify-start duration-100 items-center py-4 px-3 ease-in-out hover:bg-[#007BFF] hover:text-white"
      >
        <div className="w-10 h-10 pl-4 pt-1">{icon}</div>
        <span className={`ml-6 overflow-hidden text-lg ${sidebarExpanded ? 'w-32' : 'w-0'}`}>
          {label}
        </span>
      </Link>
    )
  }

  const MobileNavItem = ({ to, icon, label, condition = true }) => {
    if (!condition) return null
    const isActive = location.pathname.startsWith(to)
    return (
      <Link
        to={to}
        className={`flex flex-col items-center justify-center gap-1 py-2 px-2 rounded-lg min-w-0 flex-1 ${
          isActive ? 'text-blue-600' : 'text-gray-600'
        }`}
      >
        {icon}
        <span className="text-[11px] leading-none truncate">{label}</span>
      </Link>
    )
  }

  useEffect(() => {
    // Close mobile nav after navigation.
    setIsMobileNavOpen(false)
    setIsExpanded(false)
  }, [location.pathname])

  return (
    <>
      <SearchModal open={isSearchOpen} onClose={() => setIsSearchOpen(false)} />

      <nav className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] md:hidden">
        <div className="flex items-stretch justify-between px-2 pb-[max(env(safe-area-inset-bottom),0.25rem)] pt-1">
          {user && user.permissions.length > 0 && (
            <>
              <MobileNavItem to="/home" icon={<FaHome size={20} />} label="Home" />
              <button
                onClick={() => setIsSearchOpen(true)}
                className="flex flex-col items-center justify-center gap-1 py-2 px-2 rounded-lg min-w-0 flex-1 text-gray-600"
              >
                <FaSearch size={20} />
                <span className="text-[11px] leading-none truncate">Search</span>
              </button>
              <MobileNavItem
                to="/gallery"
                icon={<FaImage size={20} />}
                label="Gallery"
                condition={user.permissions.includes('artist') || user.permissions.includes('curator') || user.permissions.includes('admin')}
              />
              <MobileNavItem
                to="/editor"
                icon={<FaEdit size={20} />}
                label="Editor"
                condition={user.permissions.includes('curator') || user.permissions.includes('artist')}
              />
              <MobileNavItem to="/artozphere" icon={<FaStore size={20} />} label="Artozphere" />
              <MobileNavItem to="/blog" icon={<FaBlog size={20} />} label="Blogs" />
              <MobileNavItem
                to="/settings"
                icon={<FaCog size={20} />}
                label="Admin"
                condition={user.permissions.includes('admin')}
              />
              <MobileNavItem to="/user-settings" icon={<FaUser size={20} />} label="Profile" />
            </>
          )}

          {!user && (
            <>
              <MobileNavItem to="/about" icon={<FaHome size={20} />} label="About" />
              <MobileNavItem to="/login" icon={<FaSignOutAlt size={20} />} label="Login" />
            </>
          )}
        </div>
      </nav>

      <header
        className="fixed top-0 left-0 h-full z-10 hidden md:block"
        onMouseEnter={() => {
          if (!isMobileNavOpen) setIsExpanded(true)
        }}
        onMouseLeave={() => {
          if (!isMobileNavOpen) setIsExpanded(false)
        }}
      >
        <div
          className={`flex flex-col h-full bg-white shadow-lg transition-all duration-100 md:translate-x-0 ${isMobileNavOpen ? 'translate-x-0' : '-translate-x-full'} ${sidebarExpanded ? 'w-56' : 'w-20'}`}
        >
          <Link to={user ? "/home" : "/"} className="p-2">
            <img src={logo} alt="logo" className={`${sidebarExpanded ? 'w-[150px]' : 'w-[70px]'} my-2 mx-auto`} />
          </Link>

          <nav className="flex flex-col flex-grow linkFont">
            {user && user.permissions.length > 0 && (
              <div className="flex flex-col flex-grow">
                <NavItem to="/home" icon={<FaHome size={28} />} label="Home" />
                <button
                  onClick={() => setIsSearchOpen(true)}
                  className="flex items-center justify-start w-full px-3 py-4 duration-100 ease-in-out hover:bg-[#007BFF] hover:text-white"
                >
                  <div className="w-10 h-10 pt-1 pl-4"><FaSearch size={28} /></div>
                  <span className={`ml-6 overflow-hidden text-lg text-left ${sidebarExpanded ? 'w-32' : 'w-0'}`}>
                    Search
                  </span>
                </button>
                <NavItem
                  to="/gallery"
                  icon={<FaImage size={28} />}
                  label="Gallery"
                  condition={user.permissions.includes('artist') || user.permissions.includes('curator') || user.permissions.includes('admin')}
                />
                <NavItem
                  to="/editor"
                  icon={<FaEdit size={28} />}
                  label="Editor"
                  condition={user.permissions.includes('curator') || user.permissions.includes('artist')}
                />
                <NavItem
                  to="/artozphere"
                  icon={<FaStore size={28} />}
                  label="Artozphere"
                  condition={true}
                />
                <NavItem
                  to="/blog"
                  icon={<FaBlog size={28} />}
                  label="Blogs"
                  condition={true}
                />
                <NavItem
                  to="/settings"
                  icon={<FaCog size={28} />}
                  label="Administration"
                  condition={user.permissions.includes('admin')}
                />
              </div>
            )}

            <div className="mt-auto">
              {user && (
                <div className="py-3 px-2">
                  <Link
                    className={`flex items-center ${
                      sidebarExpanded ? 'justify-start pl-4' : 'justify-center'
                    } transition-all duration-300`}
                    to="/user-settings"
                  >
                    {userPhoto ? (
                      <img
                        src={userPhoto}
                        alt={`${user.firstName || 'User'} avatar`}
                        className="w-10 h-10 rounded-full object-cover border border-gray-200"
                      />
                    ) : (
                      <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 text-white text-lg font-semibold">
                        {userInitial}
                      </div>
                    )}
                    <span
                      className={`text-base font-medium transition-all duration-300 overflow-hidden ${
                        sidebarExpanded ? 'ml-6 opacity-100 w-32' : 'ml-0 opacity-0 w-0'
                      }`}
                    >
                      {user.firstName}
                    </span>
                  </Link>
                  <button
                    className="flex items-center justify-start w-full py-4 px-3 mt-3 duration-100 ease-in-out rounded-xl hover:bg-red-500 hover:text-white"
                    onClick={handleClick}
                  >
                    <div className="w-10 h-10 pl-4 pt-1">
                      <FaSignOutAlt size={28} />
                    </div>
                    <span className={`ml-6 overflow-hidden text-lg ${sidebarExpanded ? 'w-16' : 'w-0'}`}>
                      Logout
                    </span>
                  </button>
                </div>
              )}

              {!user && (
                <div className="flex flex-col py-2">
                  <NavItem to="/login" icon={<FaSignOutAlt size={28} />} label="Login" />
                  <NavItem to="/about" icon={<FaHome size={28} />} label="About Us" />
                </div>
              )}
            </div>
          </nav>
        </div>
      </header>
    </>
  )
}

export default Navbar